import {
  ascendingOrderValidation,
  CoreInputModelUtil,
  DecimalRegex,
  getNewErrors,
  InputErrorModel,
  nameof,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { autorun, observable } from "mobx";
import {
  IAPICreateUpdatePriceModel,
  IAPICustomPricing,
  IAPIItemBenefitsRequest,
  IAPIItemBenefitValueMappingRequest,
  IAPIItemProgramAndLevelsRequest,
  IAPIPackagePricing,
  IAPIPackagePricingOptions,
  IAPIPriceCancellationTerms,
  IAPIPriceTermSettings,
  IAPIRecurrencePriceSettings,
  IAPIStandardPricing,
  IAPITierPricing,
  IAPITierSettings,
} from "../interfaces";
import {
  BillingStartFromInterval,
  ItemBillingEnds,
  ItemBillingEndsAfterInterval,
  ItemBillingStartAfterInterval,
  ItemBillingType,
  ItemIncreasePriceBy,
  ItemIncreasePriceInterval,
  PricingModel,
  RecurringBillingInterval,
  RecurringFeeInterval,
  TierMode,
} from "../enums";
import { BenefitsModel } from "./BenefitsModel";

export class PriceTierInputData {
  @observable price: string;
  @observable showOriginalPrice: boolean;
  @observable originalPrice: string;
  errors: any[];
  @observable options: PriceTierInputOptions[];
  tierErrorIndexes: number[];
  @observable setUpFee: string;
  @observable showSetUpFee?: boolean;
  @observable isSetUpFeeRequired?: boolean;
  constructor(data?: Partial<PriceTierInputData>) {
    this.price = data?.price || "";
    this.originalPrice = data?.originalPrice || "";
    this.showOriginalPrice = data?.showOriginalPrice === true;
    this.errors = data?.errors || [];
    this.setUpFee = data?.setUpFee || "";
    this.showSetUpFee = data?.showSetUpFee;
    this.isSetUpFeeRequired = data?.isSetUpFeeRequired || false;
    this.options =
      data?.options?.map((o) => {
        return new PriceTierInputOptions(o);
      }) || [];
    this.tierErrorIndexes = data?.tierErrorIndexes || [];
    autorun(() => {
      if (!this.showOriginalPrice) {
        this.originalPrice = "";
      }
      this.validate(true);
      this.generateUnitNumber();
    });
  }

  private generateUnitNumber() {
    const options = this.options;
    options.forEach(function (x, i) {
      if (i > 0) {
        x.startValue = parseInt(options[i - 1].endValue) + 1;
      }
    });
  }

  private get priceError(): boolean {
    if (!this.price) return true;

    if (!DecimalRegex.test(this.price)) {
      return true;
    }

    return false;
  }

  private get setupFeeError(): boolean {
    if (!this.showSetUpFee) return false;

    if (!DecimalRegex.test(this.setUpFee)) {
      return true;
    }

    return false;
  }

  private get originalPriceError(): boolean {
    if (!this.showOriginalPrice) return false;
    if (!this.originalPrice) return true;

    if (!DecimalRegex.test(this.originalPrice)) {
      return true;
    }

    return false;
  }

  validate = (isAutoRun = false): boolean => {
    const newErrors: any[] = [];

    this.options?.forEach((x, idx) => {
      if (x.discountError()) {
        newErrors.push({
          index: idx,
          field: nameof<PriceTierInputOptions>("discountPercentage"),
        });
      }
      if (x.endValueError()) {
        newErrors.push({
          index: idx,
          field: nameof<PriceTierInputOptions>("endValue"),
        });
      }
    });
    if (this.priceError) {
      newErrors.push({
        index: -1,
        field: nameof<PriceTierInputData>("price"),
      });
    }

    if (this.originalPriceError) {
      newErrors.push({
        index: -2,
        field: nameof<PriceTierInputData>("originalPrice"),
      });
    }

    // if (this.setupFeeError && this.isSetUpFeeRequired) {
    //   newErrors.push({
    //     index: -1,
    //     field: nameof<PriceTierInputData>("setUpFee"),
    //   });
    // }

    const values = this.options.map((x) => {
      return parseFloat(x.endValue) || 0;
    });

    const tierErrorIndexes = ascendingOrderValidation(values, true);

    //DO NOT ADD ANY NEW ERROR
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
      this.tierErrorIndexes = getNewErrors(
        tierErrorIndexes,
        this.tierErrorIndexes
      );
    } else {
      this.errors = newErrors;
      this.tierErrorIndexes = tierErrorIndexes;
    }
    return this.errors.length === 0 && this.tierErrorIndexes.length === 0;
  };

  createAPIModel(): IAPITierPricing {
    return {
      mode: TierMode.Volume,
      originalPrice: parseFloat(this.originalPrice) || 0,
      showOriginalPrice: this.showOriginalPrice,
      price: parseFloat(this.price),
      tiers: this.options.map((d) => {
        return d.createAPIModel();
      }),
      setupFee: this.isSetUpFeeRequired ? parseFloat(this.setUpFee) : 0,
    } as IAPITierPricing;
  }

  static desrializeAPI(data: IAPITierPricing): PriceTierInputData {
    const mappedData: Partial<PriceTierInputData> = {
      price: data.price ? UtilityMethods.fixNumber(data.price) : "",
      showOriginalPrice: data.showOriginalPrice,
      originalPrice: data.originalPrice
        ? UtilityMethods.fixNumber(data.originalPrice)
        : "",
      options:
        data.tiers?.map((t, index) => {
          return PriceTierInputOptions.desrializeAPI(t);
        }) || [],
      setUpFee: data.setupFee ? UtilityMethods.fixNumber(data.setupFee) : "",
      showSetUpFee: true,
    };
    return new PriceTierInputData(mappedData);
  }

  static getDefaultTieredPricing(): PriceTierInputData {
    return new PriceTierInputData({
      options: [
        new PriceTierInputOptions({
          startValue: 1,
          endValue: "9",
          discountPercentage: "0",
        }),
        new PriceTierInputOptions({ startValue: 10, endValue: "∞" }),
      ],
      showSetUpFee: true,
    });
  }
}

export class PriceTierInputOptions {
  @observable discountPercentage: string;
  @observable startValue: number;
  @observable endValue: string;
  @observable setUpFee: string;
  @observable showSetUpFee?: boolean;
  constructor(data?: Partial<PriceTierInputOptions>) {
    this.discountPercentage = (data && data.discountPercentage) || "";
    this.startValue = data?.startValue || 1;
    this.endValue = data?.endValue || "";
    this.setUpFee = data?.setUpFee || "0";
    this.showSetUpFee = data?.showSetUpFee;
  }

  static desrializeAPI(
    data: IAPITierSettings
    // index: number,
    // previous?: IAPITierSettings
  ): PriceTierInputOptions {
    const mappedData: Partial<PriceTierInputOptions> = {
      discountPercentage: data.discount.toString(),
      endValue: data.upTo === -1 ? "∞" : data.upTo.toString(),
      startValue: data.startFrom, //index === 0 ? 1 : (previous?.upTo || 0) + 1,
    };
    return new PriceTierInputOptions(mappedData);
  }

  cost = (totalPrice: number) => {
    const cost = (parseFloat(this.discountPercentage) / 100) * totalPrice;
    return Number.isNaN(cost) ? "" : `$ ${(totalPrice - cost).toFixed(2)}`;
  };

  endValueError = (): boolean => {
    if (!this.endValue) return true;

    if (this.endValue !== "∞") {
      if (!DecimalRegex.test(this.endValue)) {
        return true;
      }
    }
    return false;
  };

  discountError = (): boolean => {
    if (!this.discountPercentage && this.startValue !== 1) return true;

    if (!DecimalRegex.test(this.discountPercentage)) {
      return true;
    } else if (parseFloat(this.discountPercentage) > 100) {
      return true;
    }

    return false;
  };

  createAPIModel(): IAPITierSettings {
    return {
      discount: parseFloat(this.discountPercentage),
      upTo: this.endValue === "∞" ? -1 : parseFloat(this.endValue),
      startFrom: this.startValue,
    } as IAPITierSettings;
  }
}

export class PackagePricingInputModel {
  @observable showOriginalPrice: boolean;
  @observable showCost: boolean;
  @observable options: PackagePricingInputDataModel[];
  @observable setUpFee: string;
  @observable showSetUpFee?: boolean;
  @observable isSetUpFeeRequired?: boolean;
  errors: any[];
  constructor(data?: Partial<PackagePricingInputModel>) {
    this.showOriginalPrice = data?.showOriginalPrice === true;
    this.showCost = data?.showCost === true;
    this.setUpFee = data?.setUpFee || "";
    this.showSetUpFee = data?.showSetUpFee;
    this.isSetUpFeeRequired = data?.isSetUpFeeRequired || false;
    this.options =
      data?.options?.map((o) => {
        return new PackagePricingInputDataModel(o);
      }) || [];
    this.errors = data?.errors || [];
    autorun(() => {
      if (this.options?.length > 0) {
        this.options.forEach((x) => {
          if (!this.showCost) {
            x.cost = "";
          }
          if (!this.showOriginalPrice) {
            x.originalPrice = "";
          }
        });
      }
      this.validate(true);
    });
  }

  private validateInput(
    idx: number,
    allValues: number[],
    value: string,
    key: keyof PackagePricingInputDataModel,
    errors: any[]
  ) {
    if (!value || !DecimalRegex.test(value)) {
      errors.push({
        field: nameof<PackagePricingInputDataModel>(key),
        index: idx,
      });
    } else if (parseFloat(value) <= 0) {
      errors.push({
        field: nameof<PackagePricingInputDataModel>(key),
        index: idx,
      });
    } else {
      const errorIndexes = ascendingOrderValidation(allValues);
      if (errorIndexes.indexOf(idx) > -1) {
        errors.push({
          field: nameof<PackagePricingInputDataModel>(key),
          index: idx,
        });
      }
    }
  }

  validate = (isAutoRun = false): boolean => {
    const errors: any[] = [];
    this.options?.forEach((option, idx) => {
      const units = this.options.map((o) => {
        return parseFloat(o.units);
      });
      const prices = this.options.map((o) => {
        return parseFloat(o.price);
      });
      this.validateInput(idx, units, option.units, "units", errors);
      this.validateInput(idx, prices, option.price, "price", errors);
      if (this.showOriginalPrice) {
        this.validateInput(
          idx,
          [],
          option.originalPrice,
          "originalPrice",
          errors
        );
      }

      if (this.showCost) {
        this.validateInput(idx, [], option.cost, "cost", errors);
      }
    });

    // if (this.showSetUpFee && this.isSetUpFeeRequired) {
    //   errors.push({
    //     field: nameof<PackagePricingInputModel>("setUpFee"),
    //     index: -1,
    //   });
    //   //  else if (parseFloat(this.setUpFee) <= 0) {
    //   //     errors.push({
    //   //         field: nameof<PackagePricingInputModel>('setUpFee'),
    //   //         index: -1,
    //   //     });
    //   // }
    // }

    if (isAutoRun) {
      this.errors = getNewErrors(errors, this.errors);
    } else {
      this.errors = errors;
    }

    return this.errors.length === 0;
  };

  static desrializeAPI(data: IAPIPackagePricing): PackagePricingInputModel {
    const mappedData: Partial<PackagePricingInputModel> = {
      showOriginalPrice: data.showOriginalPrice,
      showCost: data.showCost,
      options: data.options?.map((o) => {
        return PackagePricingInputDataModel.deserializeAPIModel(o);
      }),
      setUpFee:
        data.setupFee != undefined
          ? UtilityMethods.fixNumber(data.setupFee)
          : "",
    };
    return new PackagePricingInputModel(mappedData);
  }

  createAPIModel(): IAPIPackagePricing {
    return {
      showOriginalPrice: this.showOriginalPrice,
      showCost: this.showCost,
      options: this.options.map((o) => {
        return o.createAPIModel();
      }),
      setupFee:
        this.showSetUpFee && this.isSetUpFeeRequired
          ? parseFloat(this.setUpFee)
          : 0,
      showSetUpFee: true,
    } as IAPIPackagePricing;
  }

  static getDefaultPackagePricing(): PackagePricingInputModel {
    return new PackagePricingInputModel({
      showCost: false,
      showOriginalPrice: false,
      options: [new PackagePricingInputDataModel()],
      showSetUpFee: true,
    });
  }
}

export class PackagePricingInputDataModel {
  units: string;
  price: string;
  originalPrice: string;
  cost: string;
  constructor(data?: Partial<PackagePricingInputDataModel>) {
    this.units = (data && data.units) || "";
    this.price = (data && data.price) || "";
    this.originalPrice = (data && data.originalPrice) || "";
    this.cost = (data && data.cost) || "";
  }

  createAPIModel(): IAPIPackagePricingOptions {
    return {
      units: parseFloat(this.units),
      price: parseFloat(this.price),
      originalPrice: parseFloat(this.originalPrice) || 0,
      cost: this.cost ? parseFloat(this.cost) : 0,
    } as IAPIPackagePricingOptions;
  }

  static deserializeAPIModel(data: IAPIPackagePricingOptions) {
    const mappedData: Partial<PackagePricingInputDataModel> = {
      units: data.units ? data.units.toString() : "",
      price: data.price ? UtilityMethods.fixNumber(data.price) : "",
      originalPrice:
        data.originalPrice != undefined
          ? UtilityMethods.fixNumber(data.originalPrice)
          : "",
      cost: data.cost != undefined ? UtilityMethods.fixNumber(data.cost) : "",
    };
    return new PackagePricingInputDataModel(mappedData);
  }
}

export class CustomPricingInputModel {
  @observable minimum: string;
  @observable maximum: string;
  @observable suggestion: string;
  @observable cost: string;
  @observable setUpFee: string;
  @observable showSetUpFee?: boolean;
  @observable isSetUpFeeRequired?: boolean;
  errors: any[];
  constructor(data?: Partial<CustomPricingInputModel>) {
    this.minimum = (data && data.minimum) || "";
    this.maximum = (data && data.maximum) || "";
    this.suggestion = (data && data.suggestion) || "";
    this.cost = (data && data.cost) || "";
    this.setUpFee = data?.setUpFee || "0";
    this.showSetUpFee = data?.showSetUpFee;
    this.isSetUpFeeRequired = data?.isSetUpFeeRequired || false;
    this.errors = data?.errors || [];
    autorun(() => this.validate(true));
  }

  static desrializeAPI(data: IAPICustomPricing): CustomPricingInputModel {
    const mappedData: Partial<CustomPricingInputModel> = {
      minimum: data.minimum ? UtilityMethods.fixNumber(data.minimum) : "",
      maximum: data.maximum ? UtilityMethods.fixNumber(data.maximum) : "",
      suggestion: data.preset ? UtilityMethods.fixNumber(data.preset) : "",
      cost: data.cost ? UtilityMethods.fixNumber(data.cost) : "",
      setUpFee: data.setupFee ? UtilityMethods.fixNumber(data.setupFee) : "",
    };

    return new CustomPricingInputModel(mappedData);
  }

  validate = (isAutoRun = false): boolean => {
    const errors: any[] = [];
    // CoreInputModelUtil.validateDecimalValue(this.minimum, 'minimum', errors, undefined, true);
    // CoreInputModelUtil.validateDecimalValue(this.maximum, 'maximum', errors, undefined, true);
    // CoreInputModelUtil.validateDecimalValue(this.suggestion, 'suggestion', errors, undefined, true);
    // CoreInputModelUtil.validateDecimalValue(this.cost, 'cost', errors, undefined, true);

    if (this.showSetUpFee && this.isSetUpFeeRequired) {
      CoreInputModelUtil.validateDecimalValue(
        this.setUpFee,
        "setUpFee",
        errors
      );
    }

    if (isAutoRun) {
      this.errors = getNewErrors(errors, this.errors);
    } else {
      this.errors = errors;
    }
    return this.errors.length === 0;
  };

  createAPIModel(): IAPICustomPricing {
    const parseOrDefault = (value: string): number | undefined =>
      value?.trim() === "" || value == null ? undefined : parseFloat(value);

    return {
      minimum: parseOrDefault(this.minimum),
      maximum: parseOrDefault(this.maximum),
      preset: parseOrDefault(this.suggestion),
      cost: parseOrDefault(this.cost),
      setupFee:
        this.isSetUpFeeRequired || this.setUpFee
          ? parseOrDefault(this.setUpFee)
          : undefined,
    } as IAPICustomPricing;
  }

  static getDefaultStandardPricing(): CustomPricingInputModel {
    return new CustomPricingInputModel({
      minimum: "",
      maximum: "",
      suggestion: "",
      cost: "",
      showSetUpFee: true,
      setUpFee: "",
    });
  }
}

export class StandardPricingInputModel {
  @observable price: string;
  @observable originalPrice: string;
  @observable showOriginalPrice: boolean;
  @observable cost: string;
  @observable setUpFee: string;
  @observable showSetUpFee?: boolean;
  @observable isSetUpFeeRequired?: boolean;
  errors: any[];
  constructor(data?: Partial<StandardPricingInputModel>) {
    this.price = (data && data.price) || "";
    this.originalPrice = (data && data.originalPrice) || "";
    this.cost = (data && data.cost) || "";
    this.errors = data?.errors || [];
    this.showOriginalPrice = data?.showOriginalPrice === true;
    this.showSetUpFee = data?.showSetUpFee;
    this.isSetUpFeeRequired = data?.isSetUpFeeRequired || false;
    this.setUpFee = (data && data.setUpFee) || "";
    autorun(() => {
      if (!this.showOriginalPrice) {
        this.originalPrice = "";
      }
      this.validate(true);
    });
  }

  validate = (isAutoRun = false): boolean => {
    const errors: any[] = [];
    CoreInputModelUtil.validateDecimalValue(
      this.price,
      "price",
      errors,
      undefined,
      true
    );
    if (this.showOriginalPrice) {
      CoreInputModelUtil.validateDecimalValue(
        this.originalPrice,
        "originalPrice",
        errors,
        undefined,
        true
      );
    }

    if (this.cost) {
      CoreInputModelUtil.validateDecimalValue(
        this.cost,
        "cost",
        errors,
        undefined,
        false
      );
    }

    // if (this.showSetUpFee && this.isSetUpFeeRequired) {
    //   CoreInputModelUtil.validateDecimalValue(
    //     this.setUpFee,
    //     "setUpFee",
    //     errors
    //   );
    // }

    if (isAutoRun) {
      this.errors = getNewErrors(errors, this.errors);
    } else {
      this.errors = errors;
    }
    return this.errors.length === 0;
  };

  createAPIModel(): IAPIStandardPricing {
    return {
      price: parseFloat(this.price),
      originalPrice: this.showOriginalPrice
        ? parseFloat(this.originalPrice)
        : 0,
      cost: !this.cost ? 0 : parseFloat(this.cost),
      showOriginalPrice: this.showOriginalPrice,
      setupFee: this.showSetUpFee ? parseFloat(this.setUpFee) : undefined,
      showSetUpFee: this.showSetUpFee,
    } as IAPIStandardPricing;
  }

  static desrializeAPI(
    data: IAPIStandardPricing,
    showSetUpFee: boolean = true
  ): StandardPricingInputModel {
    const mappedData: Partial<StandardPricingInputModel> = {
      price: UtilityMethods.fixNumber(data.price),
      originalPrice: UtilityMethods.fixNumber(data.originalPrice),
      showOriginalPrice: data.showOriginalPrice,
      cost: data.cost != undefined ? UtilityMethods.fixNumber(data.cost) : "",
      setUpFee:
        data.setupFee != undefined
          ? UtilityMethods.fixNumber(data.setupFee)
          : "",
      showSetUpFee: showSetUpFee,
    };
    return new StandardPricingInputModel(mappedData);
  }

  static getDefaultStandardPricing(): StandardPricingInputModel {
    return new StandardPricingInputModel({
      price: "",
      originalPrice: "",
      showOriginalPrice: false,
      cost: "",
      showSetUpFee: true,
      setUpFee: "",
    });
  }
}

export class PricingPageInputModel {
  referenceId: string;
  id: string;
  @observable name: string;
  isOpen: boolean;
  @observable description: string;
  isActive: boolean;
  isDefault: boolean;
  billingType: keyof typeof ItemBillingType;
  @observable termSettings?: PricePageTermsInputModel | null;
  isTermsOpen: boolean;
  isRecurringOpen: boolean;
  isAdvancedOpen: boolean;
  @observable recurringSettings?: PricePageRecurringInputModel | null;
  errors: any[];
  constructor(data?: Partial<PricingPageInputModel>) {
    this.referenceId = data?.referenceId || "";
    this.id = data?.id || "";
    this.isOpen = data?.isOpen === true;
    this.name = (data && data.name) || "";
    this.description = (data && data.description) || "";
    this.isActive = data?.isActive === true;
    this.isDefault = data?.isDefault === true;
    this.billingType = (data && data.billingType) || ItemBillingType.Term;
    this.termSettings =
      (data &&
        data.termSettings &&
        new PricePageTermsInputModel(data.termSettings)) ||
      null;
    this.isTermsOpen = data?.isTermsOpen === true;
    this.isRecurringOpen = data?.isRecurringOpen === true;
    this.isAdvancedOpen = data?.isAdvancedOpen === true;
    this.recurringSettings =
      (data &&
        data.recurringSettings &&
        new PricePageRecurringInputModel(data.recurringSettings)) ||
      null;
    this.errors = data?.errors || [];

    autorun(() => this.validate(true));
  }

  validate = (isAutoRun = false): boolean => {
    const errors: any[] = [];
    CoreInputModelUtil.validateTextField(this.name, "name", errors);
    let isValid = false;

    switch (this.billingType) {
      case ItemBillingType.Term:
        isValid = this.termSettings?.validate(isAutoRun) || false;
        break;
      case ItemBillingType.Recurring:
        isValid = this.recurringSettings?.validate(isAutoRun) || false;
        break;
      case ItemBillingType.TermPlusRecurring:
        const isRecurringValid =
          this.recurringSettings?.validate(isAutoRun) || false;
        const isTermsValid = this.termSettings?.validate(isAutoRun) || false;
        isValid = isTermsValid && isRecurringValid;
        break;
    }
    if (isAutoRun) {
      this.errors = getNewErrors(errors, this.errors);
    } else {
      this.errors = errors;
    }
    const isValidForm = this.errors.length === 0 && isValid;
    if (!isValidForm) {
      this.isOpen = true;
    }
    return isValidForm;
  };

  createAPIModel(): IAPICreateUpdatePriceModel {
    return {
      id: this.id,
      referenceId: this.referenceId,
      isDefaultPrice: this.isDefault,
      name: this.name,
      description: this.description,
      isActive: this.isActive,
      billingType: this.billingType,
      termSettings:
        this.billingType === ItemBillingType.Term ||
        this.billingType === ItemBillingType.TermPlusRecurring
          ? this.termSettings?.createAPIModel()
          : null,
      recurrenceSettings:
        this.billingType === ItemBillingType.Recurring ||
        this.billingType === ItemBillingType.TermPlusRecurring
          ? this.recurringSettings?.createAPIModel()
          : null,
    } as IAPICreateUpdatePriceModel;
  }

  static deserializeAPIModel(
    data: IAPICreateUpdatePriceModel
  ): PricingPageInputModel {
    const mappedData: Partial<PricingPageInputModel> = {
      id: data.id,
      isOpen: false,
      isTermsOpen: true,
      isRecurringOpen: true,
      isActive: data.isActive,
      referenceId: data.referenceId,
      name: data.name,
      description: data.description,
      isDefault: data.isDefaultPrice,
      billingType: data.billingType,
      termSettings:
        data.termSettings &&
        (data.billingType === ItemBillingType.Term ||
          data.billingType === ItemBillingType.TermPlusRecurring)
          ? PricePageTermsInputModel.deserializeAPIModel(data.termSettings)
          : null,
      recurringSettings:
        data.recurrenceSettings &&
        (data.billingType === ItemBillingType.Recurring ||
          data.billingType === ItemBillingType.TermPlusRecurring)
          ? PricePageRecurringInputModel.deserializeAPIModel(
              data.recurrenceSettings
            )
          : null,
    };
    return new PricingPageInputModel(mappedData);
  }
}

export class PricePageTermsInputModel {
  //if false then assume life time
  @observable intervalApplies: boolean;
  @observable termInterval: RecurringBillingInterval;
  @observable termIntervalCount: string;
  @observable splitPayments: boolean;
  @observable initialPayment: string;
  @observable totalPayments: string;
  @observable pricingModel: PricingModel;
  @observable standardPricing?: StandardPricingInputModel | null;
  @observable packagePricing?: PackagePricingInputModel | null;
  @observable customPricing?: CustomPricingInputModel | null;
  @observable volumePricing?: PriceTierInputData | null;
  @observable tieredPricing?: PriceTierInputData | null;
  errors: InputErrorModel<PricePageTermsInputModel>[];
  constructor(data?: Partial<PricePageTermsInputModel>) {
    if (data?.intervalApplies === undefined) {
      this.intervalApplies = true;
    } else {
      this.intervalApplies = data?.intervalApplies === true;
    }

    this.termInterval =
      (data && data.termInterval) || RecurringBillingInterval.Month;
    this.termIntervalCount = (data && data.termIntervalCount) || "1";
    this.splitPayments = data?.splitPayments === true;
    this.initialPayment = (data && data.initialPayment) || "";
    this.totalPayments = (data && data.totalPayments) || "";

    this.pricingModel = (data && data.pricingModel) || PricingModel.Standard;

    switch (this.pricingModel) {
      case PricingModel.Standard:
        this.standardPricing =
          data && data.standardPricing
            ? new StandardPricingInputModel({ ...data.standardPricing })
            : StandardPricingInputModel.getDefaultStandardPricing();
        break;
      case PricingModel.Package:
        this.packagePricing = data?.packagePricing
          ? new PackagePricingInputModel({
              ...data?.packagePricing,
              showSetUpFee: true,
            })
          : PackagePricingInputModel.getDefaultPackagePricing();
        break;
      case PricingModel.Custom:
        this.customPricing =
          data && data.customPricing
            ? new CustomPricingInputModel({
                ...data?.customPricing,
                showSetUpFee: true,
              })
            : CustomPricingInputModel.getDefaultStandardPricing();
        break;
      case PricingModel.Volume:
        this.volumePricing = data?.volumePricing
          ? new PriceTierInputData({
              ...data?.volumePricing,
              showSetUpFee: true,
            })
          : PriceTierInputData.getDefaultTieredPricing();
        break;
      case PricingModel.Tiered:
        this.tieredPricing = data?.tieredPricing
          ? new PriceTierInputData({
              ...data?.tieredPricing,
              showSetUpFee: true,
            })
          : PriceTierInputData.getDefaultTieredPricing();
        break;
    }
    this.errors = data?.errors || [];
    autorun(() => this.validate(true));
  }

  validate = (isAutoRun = false): boolean => {
    const errors: any[] = [];

    if (this.splitPayments) {
      CoreInputModelUtil.validateDecimalValue(
        this.initialPayment,
        "initialPayment",
        errors,
        undefined,
        true
      );
      CoreInputModelUtil.validateDecimalValue(
        this.totalPayments,
        "totalPayments",
        errors,
        undefined,
        true
      );
    }

    if (this.intervalApplies) {
      if (!this.termInterval) {
        CoreInputModelUtil.addError(
          nameof<PricePageTermsInputModel>("termInterval"),
          errors
        );
      } else {
        switch (this.termInterval) {
          case RecurringBillingInterval.Month:
            if (
              parseFloat(this.termIntervalCount) > 12 ||
              parseFloat(this.termIntervalCount) < 1
            ) {
              CoreInputModelUtil.validateDecimalValue(
                this.termIntervalCount,
                "termIntervalCount",
                errors,
                undefined,
                true
              );
            }
            break;
          case RecurringBillingInterval.Week:
            if (
              parseFloat(this.termIntervalCount) > 52 ||
              parseFloat(this.termIntervalCount) < 1
            ) {
              CoreInputModelUtil.validateDecimalValue(
                this.termIntervalCount,
                "termIntervalCount",
                errors,
                undefined,
                true
              );
            }
            break;
          case RecurringBillingInterval.Day:
            if (
              parseFloat(this.termIntervalCount) > 31 ||
              parseFloat(this.termIntervalCount) < 1
            ) {
              CoreInputModelUtil.validateDecimalValue(
                this.termIntervalCount,
                "termIntervalCount",
                errors,
                undefined,
                true
              );
            }
            break;
        }
      }
    }

    let isValidPrice = false;
    switch (this.pricingModel) {
      case PricingModel.Standard:
        isValidPrice = this.standardPricing?.validate(isAutoRun) || false;
        break;
      case PricingModel.Package:
        isValidPrice = this.packagePricing?.validate(isAutoRun) || false;
        break;
      case PricingModel.Volume:
        isValidPrice = this.volumePricing?.validate(isAutoRun) || false;
        break;
      case PricingModel.Tiered:
        isValidPrice = this.tieredPricing?.validate(isAutoRun) || false;
        break;
      case PricingModel.Custom:
        isValidPrice = this.customPricing?.validate(isAutoRun) || false;
        break;
    }
    if (isAutoRun) {
      this.errors = getNewErrors(errors, this.errors);
    } else {
      this.errors = errors;
    }
    return this.errors.length === 0 && isValidPrice;
  };

  createAPIModel(): IAPIPriceTermSettings {
    return {
      intervalApplies: this.intervalApplies,
      termInterval: this.termInterval,
      termIntervalCount: parseFloat(this.termIntervalCount),
      splitPayments: this.splitPayments,
      initialPayment: this.splitPayments ? parseFloat(this.initialPayment) : 0,
      totalPayments: this.splitPayments ? parseFloat(this.totalPayments) : 0,
      pricingModel: this.pricingModel,
      standardPricing: this.standardPricing?.createAPIModel(),
      packagePricing: this.packagePricing?.createAPIModel(),
      customPricing: this.customPricing?.createAPIModel(),
      tierPricing:
        this.pricingModel === PricingModel.Tiered
          ? this.tieredPricing?.createAPIModel()
          : this.pricingModel === PricingModel.Volume
            ? this.volumePricing?.createAPIModel()
            : null,
    } as IAPIPriceTermSettings;
  }

  static deserializeAPIModel(
    data: IAPIPriceTermSettings
  ): PricePageTermsInputModel {
    const mappedData: Partial<PricePageTermsInputModel> = {
      intervalApplies: data.intervalApplies,
      termInterval: data.termInterval,
      termIntervalCount: data.termIntervalCount.toString(),
      splitPayments: data.splitPayments,
      initialPayment: data.initialPayment.toString(),
      totalPayments: data.totalPayments.toString(),
      pricingModel: data.pricingModel,
      standardPricing: data.standardPricing
        ? StandardPricingInputModel.desrializeAPI(data.standardPricing)
        : null,
      packagePricing: data.packagePricing
        ? PackagePricingInputModel.desrializeAPI(data.packagePricing)
        : null,
      customPricing: data.customPricing
        ? CustomPricingInputModel.desrializeAPI(data.customPricing)
        : null,
      volumePricing:
        data.pricingModel === PricingModel.Volume && data.tierPricing
          ? PriceTierInputData.desrializeAPI(data.tierPricing)
          : null,
      tieredPricing:
        data.pricingModel === PricingModel.Tiered &&
        data.tierPricing &&
        data.tierPricing
          ? PriceTierInputData.desrializeAPI(data.tierPricing)
          : null,
    };
    return new PricePageTermsInputModel(mappedData);
  }
}

export class PricePageRecurringInputModel {
  splitPayments: boolean;
  prorateFirstPayment: boolean;
  initialPayment: string;
  totalPayments: string;
  billingInterval: keyof typeof RecurringBillingInterval;
  billingIntervalCount: string;
  billingStartInterval: keyof typeof BillingStartFromInterval;
  billingStartAfterInterval: keyof typeof ItemBillingStartAfterInterval;
  billingStartIntervalCount: string;
  pricingModel: PricingModel;
  standardPricing?: StandardPricingInputModel | null;
  packagePricing?: PackagePricingInputModel | null;
  customPricing?: CustomPricingInputModel | null;
  volumePricing?: PriceTierInputData | null;
  billingEnds: keyof typeof ItemBillingEnds;
  billingEndIntervalCount: string;
  billingEndsAfterInterval: keyof typeof ItemBillingEndsAfterInterval;
  increasePriceValue: string;
  increasePriceInterval: keyof typeof ItemIncreasePriceInterval;
  increasePriceBy: keyof typeof ItemIncreasePriceBy;
  fee: string;
  feeInterval: keyof typeof RecurringFeeInterval;
  errors: InputErrorModel<PricePageRecurringInputModel>[];
  cancellationNoticeDays: string;
  cancellationFee: string;
  cancellationMinimumDue: string;
  tieredPricing?: PriceTierInputData | null;
  constructor(data?: Partial<PricePageRecurringInputModel>) {
    this.splitPayments = data?.splitPayments === true;
    this.prorateFirstPayment = data?.prorateFirstPayment === true;
    this.initialPayment = (data && data.initialPayment) || "";
    this.totalPayments = (data && data.totalPayments) || "";
    this.billingInterval =
      (data && data.billingInterval) || RecurringBillingInterval.Month;
    this.billingStartAfterInterval =
      (data && data.billingStartAfterInterval) ||
      ItemBillingStartAfterInterval.Month;
    this.billingStartIntervalCount =
      (data && data.billingStartIntervalCount) ||
      (this.billingStartAfterInterval === ItemBillingStartAfterInterval.Month
        ? "24"
        : "");

    this.billingIntervalCount = (data && data.billingIntervalCount) || "";
    this.billingEndIntervalCount = (data && data.billingEndIntervalCount) || "";

    this.billingStartInterval =
      (data && data.billingStartInterval) ||
      BillingStartFromInterval.Immediately;
    this.pricingModel = (data && data.pricingModel) || PricingModel.Standard;
    this.billingEnds =
      (data && data.billingEnds) ||
      (Object.keys(ItemBillingEnds).find(
        (key) =>
          ItemBillingEnds[key as keyof typeof ItemBillingEnds] ===
          ItemBillingEnds.OnCancellation
      ) as keyof typeof ItemBillingEnds);

    switch (this.pricingModel) {
      case PricingModel.Standard:
        this.standardPricing =
          data &&
          data.standardPricing &&
          new StandardPricingInputModel({
            ...data.standardPricing,
            showSetUpFee: true,
          });
        break;
      case PricingModel.Package:
        this.packagePricing = data?.packagePricing
          ? new PackagePricingInputModel({
              ...data?.packagePricing,
              showSetUpFee: true,
            })
          : PackagePricingInputModel.getDefaultPackagePricing();
        break;
      case PricingModel.Custom:
        this.customPricing =
          data &&
          data.customPricing &&
          new CustomPricingInputModel({
            ...data.customPricing,
            showSetUpFee: true,
          });
        break;
      case PricingModel.Volume:
        this.volumePricing = data?.volumePricing
          ? new PriceTierInputData({
              ...data?.volumePricing,
              showSetUpFee: true,
            })
          : PriceTierInputData.getDefaultTieredPricing();
        break;
      case PricingModel.Tiered:
        this.tieredPricing = data?.tieredPricing
          ? new PriceTierInputData({
              ...data?.tieredPricing,
              showSetUpFee: true,
            })
          : PriceTierInputData.getDefaultTieredPricing();
        break;
    }
    this.errors = data?.errors || [];
    this.increasePriceValue = (data && data.increasePriceValue) || "";
    this.increasePriceInterval =
      (data && data.increasePriceInterval) ||
      (Object.keys(ItemIncreasePriceInterval).find(
        (key) =>
          ItemIncreasePriceInterval[
            key as keyof typeof ItemIncreasePriceInterval
          ] === ItemIncreasePriceInterval.None
      ) as keyof typeof ItemIncreasePriceInterval);
    this.increasePriceBy =
      (data && data.increasePriceBy) ||
      (Object.keys(ItemIncreasePriceBy).find(
        (key) =>
          ItemIncreasePriceBy[key as keyof typeof ItemIncreasePriceBy] ===
          ItemIncreasePriceBy.Percent
      ) as ItemIncreasePriceBy);
    this.fee = (data && data.fee) || "";
    this.cancellationNoticeDays = (data && data.cancellationNoticeDays) ?? "0";
    this.cancellationFee = (data && data.cancellationFee) ?? "0";
    this.cancellationMinimumDue = (data && data.cancellationMinimumDue) ?? "0";
    this.feeInterval =
      (data && data.feeInterval) ||
      (Object.keys(RecurringFeeInterval).find(
        (key) =>
          RecurringFeeInterval[key as keyof typeof RecurringFeeInterval] ===
          RecurringFeeInterval.None
      ) as keyof typeof RecurringFeeInterval);
    this.billingEndsAfterInterval =
      (data && data.billingEndsAfterInterval) ||
      (Object.keys(ItemBillingEndsAfterInterval).find(
        (key) =>
          ItemBillingEndsAfterInterval[
            key as keyof typeof ItemBillingEndsAfterInterval
          ] === ItemBillingEndsAfterInterval.Payments
      ) as keyof typeof ItemBillingEndsAfterInterval);

    autorun(() => this.validate(true));
  }

  isBillEveryGreaterThan3Months = (
    billingInterval: keyof typeof RecurringBillingInterval,
    billingIntervalCount: string
  ): boolean => {
    if (!billingInterval || !billingIntervalCount) return false;

    const daysPerUnit = {
      [RecurringBillingInterval.Day]: 1,
      [RecurringBillingInterval.Week]: 7,
      [RecurringBillingInterval.Month]: 30,
      [RecurringBillingInterval.Year]: 365,
    };

    const totalDays =
      parseFloat(billingIntervalCount) * (daysPerUnit[billingInterval] || 0);

    return totalDays >= 90;
  };

  validate = (isAutoRun = false): boolean => {
    const errors: any[] = [];
    let isPricingModelValid = false;
    if (this.splitPayments) {
      CoreInputModelUtil.validateDecimalValue(
        this.initialPayment,
        "initialPayment",
        errors
      );
      CoreInputModelUtil.validateDecimalValue(
        this.totalPayments,
        "totalPayments",
        errors
      );
    }
    CoreInputModelUtil.validateDecimalValue(
      this.billingIntervalCount,
      "billingIntervalCount",
      errors,
      undefined,
      true
    );
    if (!this.billingInterval) {
      errors.push({
        field: nameof<PricePageRecurringInputModel>("billingInterval"),
      });
    } else {
      switch (this.billingInterval) {
        case RecurringBillingInterval.Month:
          if (parseInt(this.billingIntervalCount) > 12) {
            CoreInputModelUtil.addError(
              nameof<PricePageRecurringInputModel>("billingIntervalCount"),
              errors
            );
          }
          break;
        case RecurringBillingInterval.Day:
        case RecurringBillingInterval.Week:
        case RecurringBillingInterval.Year:
        default:
          break;
      }
    }

    if (!this.billingStartInterval) {
      CoreInputModelUtil.addError(
        nameof<PricePageRecurringInputModel>("billingStartInterval"),
        errors
      );
    }

    if (!this.billingStartAfterInterval) {
      CoreInputModelUtil.addError(
        nameof<PricePageRecurringInputModel>("billingStartAfterInterval"),
        errors
      );
    } else if (this.billingStartInterval === BillingStartFromInterval.After) {
      CoreInputModelUtil.validateDecimalValue(
        this.billingStartIntervalCount,
        "billingStartIntervalCount",
        errors,
        undefined,
        true
      );
    }

    if (
      this.isBillEveryGreaterThan3Months(
        this.billingInterval,
        this.billingIntervalCount
      )
    ) {
      switch (this.increasePriceInterval) {
        case "None":
          break;
        case "Every6Month":
        case "EveryYear":
        default:
          switch (this.increasePriceBy) {
            case ItemIncreasePriceBy.Amount:
              CoreInputModelUtil.validateDecimalValue(
                this.increasePriceValue,
                "increasePriceValue",
                errors,
                undefined,
                true
              );
              if (
                parseFloat(this.increasePriceValue) >
                parseFloat(this.standardPricing?.price || "0")
              ) {
                errors.push({
                  field:
                    nameof<PricePageRecurringInputModel>("increasePriceValue"),
                  message: "Amount can't be greater than the price",
                });
              }
              break;
            case ItemIncreasePriceBy.Percent:
              CoreInputModelUtil.validateDecimalValue(
                this.increasePriceValue,
                "increasePriceValue",
                errors,
                undefined,
                true
              );
              break;
          }
          break;
      }
    } else {
      if (this.increasePriceInterval !== "None") {
        errors.push({
          field: "increasePriceInterval",
          message:
            "Price increase can't be applied if billing interval is less than 3 months",
        });
      }
    }
    if (
      this.isBillEveryGreaterThan3Months(
        this.billingInterval,
        this.billingIntervalCount
      )
    ) {
      switch (this.feeInterval) {
        case "None":
          break;
        case "OnRenewal":
        default:
          CoreInputModelUtil.validateDecimalValue(this.fee, "fee", errors);
          if (
            parseFloat(this.fee) >
            parseFloat(this.standardPricing?.price || "0")
          ) {
            errors.push({
              field: nameof<PricePageRecurringInputModel>("fee"),
              message: "Amount can't be greater than the price",
            });
          }
          break;
      }
    } else {
      if (this.feeInterval !== "None") {
        errors.push({
          field: "feeInterval",
          message:
            "Recurring fee can't be applied if billing interval is less than 3 months",
        });
      }
    }

    CoreInputModelUtil.validateDecimalValue(
      this.cancellationNoticeDays,
      "cancellationNoticeDays",
      errors
    );
    CoreInputModelUtil.validateDecimalValue(
      this.cancellationFee,
      "cancellationFee",
      errors
    );
    CoreInputModelUtil.validateDecimalValue(
      this.cancellationMinimumDue,
      "cancellationMinimumDue",
      errors
    );
    if (!this.billingEnds) {
      errors.push({
        field: nameof<PricePageRecurringInputModel>("billingEnds"),
      });
    } else if (this.billingEnds === ItemBillingEnds.After) {
      CoreInputModelUtil.validateDecimalValue(
        this.billingEndIntervalCount,
        "billingEndIntervalCount",
        errors
      );
    }
    CoreInputModelUtil.validateDecimalValue(
      this.billingIntervalCount,
      "billingIntervalCount",
      errors
    );
    switch (this.pricingModel) {
      case PricingModel.Standard:
        isPricingModelValid =
          this.standardPricing?.validate(isAutoRun) || false;
        break;
      case PricingModel.Package:
        isPricingModelValid = this.packagePricing?.validate(isAutoRun) || false;
        break;
      case PricingModel.Volume:
        isPricingModelValid = this.volumePricing?.validate(isAutoRun) || false;
        break;
      case PricingModel.Tiered:
        isPricingModelValid = this.tieredPricing?.validate(isAutoRun) || false;
        break;
      case PricingModel.Custom:
        isPricingModelValid = this.customPricing?.validate(isAutoRun) || false;
        break;
    }
    if (isAutoRun) {
      this.errors = getNewErrors(errors, this.errors);
    } else {
      this.errors = errors;
    }
    return this.errors.length === 0 && isPricingModelValid;
  };

  createAPIModel(): IAPIRecurrencePriceSettings {
    return {
      termInterval: this.billingInterval,
      termIntervalCount: parseFloat(this.billingIntervalCount),
      splitPayments: this.splitPayments,
      initialPayment: parseFloat(this.initialPayment) || 0,
      totalPayments: parseFloat(this.totalPayments) || 0,
      billingInterval: this.billingInterval,
      billingIntervalCount: parseFloat(this.billingIntervalCount),
      billingStartInterval: this.billingStartInterval,
      billingStartAfterInterval: this.billingStartAfterInterval,
      billingStartIntervalCount:
        parseFloat(this.billingStartIntervalCount) || 0,
      billingEndsInterval: this.billingEnds,
      billingEndIntervalCount: parseFloat(this.billingEndIntervalCount) || 0,
      billingEndsAfterInterval: this.billingEndsAfterInterval,
      fee: parseFloat(this.fee) || 0,
      feeInterval: this.feeInterval,
      increasePriceAmount:
        this.increasePriceBy === "Amount"
          ? parseFloat(this.increasePriceValue) || 0
          : 0,
      increasePricePercentage:
        this.increasePriceBy === "Percent"
          ? parseFloat(this.increasePriceValue) || 0
          : 0,
      increasePriceInterval: this.increasePriceInterval,
      increasePriceBy: this.increasePriceBy,
      prorateFirstPayment: this.prorateFirstPayment,
      pricingModel: this.pricingModel,
      standardPricing: this.standardPricing?.createAPIModel(),
      packagePricing: this.packagePricing?.createAPIModel(),
      customPricing: this.customPricing?.createAPIModel(),
      tierPricing:
        this.pricingModel === PricingModel.Tiered
          ? this.tieredPricing?.createAPIModel()
          : this.pricingModel === PricingModel.Volume
            ? this.volumePricing?.createAPIModel()
            : null,
      cancellationTerms: {
        daysNotice: parseFloat(this.cancellationNoticeDays),
        cancellationFee: parseFloat(this.cancellationFee),
        minimumDue: parseFloat(this.cancellationMinimumDue),
      } as IAPIPriceCancellationTerms,
    } as IAPIRecurrencePriceSettings;
  }

  static deserializeAPIModel(
    data: IAPIRecurrencePriceSettings
  ): PricePageRecurringInputModel {
    const mappedData: Partial<PricePageRecurringInputModel> = {
      splitPayments: data.splitPayments,
      prorateFirstPayment: data.prorateFirstPayment,
      initialPayment: data.initialPayment.toString(),
      totalPayments: data.totalPayments.toString(),
      billingInterval: data.billingInterval || "Month",
      billingIntervalCount: data.billingIntervalCount.toString(),
      billingStartAfterInterval: data.billingStartAfterInterval,
      billingStartIntervalCount: data.billingStartIntervalCount.toString(),
      billingStartInterval: data.billingStartInterval,
      billingEnds: data.billingEndsInterval,
      billingEndIntervalCount: data.billingEndIntervalCount.toString(),
      billingEndsAfterInterval: data.billingEndsAfterInterval,
      fee: data.fee > 0 ? UtilityMethods.fixNumber(data.fee) : "",
      feeInterval: data.feeInterval,
      increasePriceValue:
        data.increasePriceBy === ItemIncreasePriceBy.Amount
          ? UtilityMethods.fixNumber(data.increasePriceAmount)
          : data.increasePricePercentage.toString(),
      increasePriceInterval: data.increasePriceInterval,
      increasePriceBy: data.increasePriceBy,
      pricingModel: data.pricingModel,
      standardPricing: data.standardPricing
        ? StandardPricingInputModel.desrializeAPI(data.standardPricing)
        : null,
      packagePricing: data.packagePricing
        ? PackagePricingInputModel.desrializeAPI(data.packagePricing)
        : null,
      customPricing: data.customPricing
        ? CustomPricingInputModel.desrializeAPI(data.customPricing)
        : null,
      volumePricing: data.tierPricing
        ? PriceTierInputData.desrializeAPI(data.tierPricing)
        : null,
      cancellationNoticeDays: data.cancellationTerms.daysNotice.toString(),
      cancellationFee: UtilityMethods.fixNumber(
        data.cancellationTerms.cancellationFee
      ),
      cancellationMinimumDue: UtilityMethods.fixNumber(
        data.cancellationTerms.minimumDue
      ),
    };
    return new PricePageRecurringInputModel(mappedData);
  }
}

export class PriceBenefitsInputModel {
  priceReferenceId: string;
  priceName: string;
  priceBillingType: keyof typeof ItemBillingType;
  isActivePrice: boolean;
  @observable maxValue: string;
  @observable ageLimit: boolean;
  @observable minAge: string;
  @observable maxAge: string;
  errors: any[];
  @observable termBenefits: PriceBenefitsValueMappingModel[];
  @observable recurringBenefits: PriceBenefitsValueMappingModel[];
  constructor(data?: Partial<PriceBenefitsInputModel>) {
    this.priceReferenceId = data?.priceReferenceId || "";
    this.priceBillingType =
      (data && data.priceBillingType) || ItemBillingType.Term;
    this.priceName = data?.priceName || "";
    this.isActivePrice = data?.isActivePrice === true;
    this.ageLimit = data?.ageLimit === true;
    this.maxValue = data?.maxValue || "";
    this.maxAge = data?.maxAge || "";
    this.minAge = data?.minAge || "";
    this.errors = data?.errors || [];
    this.termBenefits =
      (data?.termBenefits &&
        data.termBenefits.map((t) => {
          return new PriceBenefitsValueMappingModel(t);
        })) ||
      [];
    this.recurringBenefits =
      (data?.recurringBenefits &&
        data.recurringBenefits.map((t) => {
          return new PriceBenefitsValueMappingModel(t);
        })) ||
      [];
    autorun(() => this.validate(true));
  }

  validate = (isAutoRun = false): boolean => {
    const errors: any[] = [];
    if (!this.maxValue || !DecimalRegex.test(this.maxValue)) {
      errors.push({
        field: nameof<PriceBenefitsInputModel>("maxValue"),
      });
    } else if (parseFloat(this.maxValue) <= 0) {
      errors.push({
        field: nameof<PriceBenefitsInputModel>("maxValue"),
      });
    }

    if (this.ageLimit) {
      if (!Number.isInteger(parseInt(this.minAge))) {
        errors.push({
          field: nameof<PriceBenefitsInputModel>("minAge"),
        });
      } else if (parseInt(this.minAge) <= 0) {
        errors.push({
          field: nameof<PriceBenefitsInputModel>("minAge"),
        });
      }
      if (!Number.isInteger(parseInt(this.minAge))) {
        errors.push({
          field: nameof<PriceBenefitsInputModel>("maxAge"),
        });
      } else if (parseInt(this.maxAge) <= 0) {
        errors.push({
          field: nameof<PriceBenefitsInputModel>("maxAge"),
        });
      }
    }

    let hasErrorsInTerm = false;
    let hasErrorsInRecurring = false;
    this.termBenefits?.forEach((t) => {
      if (!t.validate(isAutoRun)) {
        hasErrorsInTerm = true;
      }
    });
    this.recurringBenefits?.forEach((t) => {
      if (!t.validate(isAutoRun)) {
        hasErrorsInRecurring = true;
      }
    });
    if (isAutoRun) {
      this.errors = getNewErrors(errors, this.errors);
    } else {
      this.errors = errors;
    }
    return (
      this.errors.length === 0 && !hasErrorsInTerm && !hasErrorsInRecurring
    );
  };

  updateBenefits(benefits: BenefitsModel[]) {
    const termBenefits: PriceBenefitsValueMappingModel[] = [];
    const recurringBenefits: PriceBenefitsValueMappingModel[] = [];
    benefits.forEach((b) => {
      const existingTermBenefits = this.termBenefits.find(
        (x) => x.benefitId === b.id
      );
      const existingRecurringBenefits = this.recurringBenefits.find(
        (x) => x.benefitId === b.id
      );
      termBenefits.push(
        new PriceBenefitsValueMappingModel({
          benefitId: b.id,
          benefitName: b.pluralName,
          value: existingTermBenefits?.value || "",
          errors: existingTermBenefits?.errors || [],
        })
      );
      recurringBenefits.push(
        new PriceBenefitsValueMappingModel({
          benefitId: b.id,
          benefitName: b.pluralName,
          value: existingRecurringBenefits?.value || "",
          errors: existingRecurringBenefits?.errors || [],
        })
      );
    });

    this.termBenefits = termBenefits;
    this.recurringBenefits = recurringBenefits;
  }

  createAPIModel(): IAPIItemBenefitsRequest {
    return {
      priceReferenceId: this.priceReferenceId,
      ageLimit: this.ageLimit,
      maxAge: this.ageLimit ? parseInt(this.maxAge) : 0,
      minAge: this.ageLimit ? parseInt(this.minAge) : 0,
      maxValue: parseFloat(this.maxValue),
      terms: this.termBenefits.map((t) => {
        return t.createAPIModel();
      }),
      recurring: this.recurringBenefits.map((t) => {
        return t.createAPIModel();
      }),
    } as IAPIItemBenefitsRequest;
  }

  static deserializeAPIModel(
    data: IAPIItemBenefitsRequest
  ): PriceBenefitsInputModel {
    const mappedData: Partial<PriceBenefitsInputModel> = {
      priceReferenceId:
        data.priceReferenceId || new Date().getTime().toString(),
      minAge: data.minAge.toString(),
      maxAge: data.maxAge.toString(),
      maxValue: data.maxValue.toString(),
      termBenefits: PriceBenefitsValueMappingModel.deserializeAPIModel(
        data.terms
      ),
      recurringBenefits: PriceBenefitsValueMappingModel.deserializeAPIModel(
        data.recurring
      ),
    };

    return new PriceBenefitsInputModel(mappedData);
  }
}

export class PriceBenefitsValueMappingModel {
  benefitId: string;
  benefitName: string;
  @observable value: string;
  errors: any[];
  constructor(data?: Partial<PriceBenefitsValueMappingModel>) {
    this.benefitId = data?.benefitId || "";
    this.benefitName = data?.benefitName || "";
    this.value = data?.value || "";
    this.errors = data?.errors || [];
    autorun(() => this.validate(true));
  }

  validate(isAutoRun = false) {
    const errors: any[] = [];
    if (!this.value || !DecimalRegex.test(this.value)) {
      errors.push({
        field: nameof<PriceBenefitsValueMappingModel>("value"),
      });
    } else if (parseFloat(this.value) <= 0) {
      errors.push({
        field: nameof<PriceBenefitsValueMappingModel>("value"),
      });
    }
    if (isAutoRun) {
      this.errors = getNewErrors(errors, this.errors);
    } else {
      this.errors = errors;
    }
    return this.errors.length === 0;
  }

  createAPIModel(): IAPIItemBenefitValueMappingRequest {
    return {
      benefitId: this.benefitId,
      value: parseFloat(this.value),
    } as IAPIItemBenefitValueMappingRequest;
  }

  static deserializeAPIModel(
    data: IAPIItemBenefitValueMappingRequest[]
  ): PriceBenefitsValueMappingModel[] {
    const list: PriceBenefitsValueMappingModel[] = [];

    if (Array.isArray(data)) {
      data.forEach((d) => {
        const mappedData: Partial<PriceBenefitsValueMappingModel> = {
          benefitId: d.benefitId,
          benefitName: d.benefitName,
          value: d.value.toString(),
        };

        list.push(new PriceBenefitsValueMappingModel(mappedData));
      });
    }

    return list;
  }
}

export class PriceProgramAndLevelsMappingModel {
  @observable programId: string;
  @observable levelId: string;
  priceReferenceId: string;
  priceName: string;
  isActivePrice: boolean;
  errors: any[];
  constructor(data?: Partial<PriceProgramAndLevelsMappingModel>) {
    this.programId = data?.programId || "";
    this.levelId = data?.levelId || "";
    this.priceReferenceId = data?.priceReferenceId || "";
    this.priceName = data?.priceName || "";
    this.isActivePrice = data?.isActivePrice === true;
    this.errors = data?.errors || [];
    autorun(() => {
      if (!this.programId && this.levelId) {
        this.levelId = "";
      }

      this.validate(true);
    });
  }

  hasError(key: keyof PriceProgramAndLevelsMappingModel): boolean {
    return this.errors.findIndex((x) => x.field === key) > -1;
  }
  validate(isAutoRun = false) {
    const errors: any[] = [];
    if (this.programId && !this.levelId) {
      errors.push({
        field: nameof<PriceProgramAndLevelsMappingModel>("levelId"),
      });
    }
    if (isAutoRun) {
      this.errors = getNewErrors(errors, this.errors);
    } else {
      this.errors = errors;
    }
    return this.errors.length === 0;
  }

  createAPIModel(): IAPIItemProgramAndLevelsRequest {
    return {
      programId: this.programId,
      levelId: this.levelId,
      priceReferenceId: this.priceReferenceId,
    } as IAPIItemProgramAndLevelsRequest;
  }

  static deserializeAPIModel(
    data: IAPIItemProgramAndLevelsRequest
  ): PriceProgramAndLevelsMappingModel {
    const mappedData: Partial<PriceProgramAndLevelsMappingModel> = {
      priceReferenceId:
        data.priceReferenceId || new Date().getTime().toString(),
      programId: data.programId || "",
      levelId: data.levelId || "",
    };

    return new PriceProgramAndLevelsMappingModel(mappedData);
  }
}
